import React from 'react';
import { LoadingIndicator, gray90 } from '@spotify-internal/encore-web-v5';
import { ModuleType } from '../types';
import { ContentList } from '../../ContentList';
import { itemToSearchHit } from '../../Search';
import type { SearchHit } from '../../Search/types';
import { Filter } from '../../ContentList/filterType';
import { EventType } from '../../Event';
import { useLocale, useT } from '@spotify-internal/i18n-core';
import { useRouter } from 'next/router';

export type ContentListModuleType = ModuleType & {
  items?: EventType[];
};

export const EventListModule: React.FC<ContentListModuleType> = ({
  id = '',
  items = [],
}) => {
  const [events, setEvents] = React.useState<SearchHit[]>();
  const [filters, setFilters] = React.useState<Filter[]>();

  const router = useRouter();
  const t = useT();
  const locale = useLocale();
  const today = new Date().toString();

  const oneDay = 86400000;

  React.useEffect(() => {
    const filteredEvents = items
      .map(item => itemToSearchHit(item, locale))
      .filter(Boolean)
      .filter(
        item =>
          item!.date && Date.parse(item!.date) >= Date.parse(today) - oneDay,
      )
      .sort((a, b) => {
        // TODO Handle these exclamation points
        return Date.parse(a!.date!) - Date.parse(b!.date!);
      }) as NonNullable<SearchHit[]>;
    setEvents(filteredEvents);

    const franchiseFilters = Array.from(
      new Set(filteredEvents.map(({ label }) => label)),
    )
      .filter(Boolean)
      .sort();

    const locationFilters = Array.from(
      new Set(filteredEvents.map(({ location }) => location)),
    )
      .filter(Boolean)
      .sort();

    setFilters([
      {
        filterValue: 'label',
        allLabel: t('FRODOR_c2b5a2', 'All event types', ''),
        filterOptions: franchiseFilters,
      },
      {
        filterValue: 'location',
        allLabel: t('FRODOR_e5a2b3', 'All locations', ''),
        filterOptions: locationFilters,
      },
    ] as NonNullable<Filter[]>);
  }, [items]);

  const emptyMessage =
    events?.length === 0 &&
    router &&
    router.query &&
    router.query._lqa === 'showEmpty'
      ? t('FRODOR_ddeaa1', 'We currently do not have any upcoming events.', '')
      : t('FRODOR_05e63d', 'No events matched the specified filters.', '');

  return (
    (events && filters && (
      <ContentList
        data-testid={`contentList--module-${id}`}
        items={events}
        filterBarTitle={t('FRODOR_8931db', 'Upcoming events', '')}
        filters={filters}
        filterBarColor={gray90}
        emptyMessage={emptyMessage}
        showFilters={events.length > 1 || router.query._lqa === 'showFilters'}
      />
    )) || <LoadingIndicator />
  );
};
